import { team } from "@/content/team";
import { Section, EyebrowHeading } from "@/components/site/Section";

/**
 * Visar teamet — namn (om satt), roll och en kort bio. På sidor där man
 * står inför att boka möte hjälper det att se vem man faktiskt kommer att
 * prata med. Om `photo` saknas visas initialer i en cirkulär platsfyllare.
 */
export function TeamStrip({
  eyebrow = "Vem träffar ni?",
  title = "Ni pratar med människorna som gör filmen — inte en säljare.",
  lede = "Ingen call center, ingen mellanhand. Samma personer sätter briefen, håller kameran och klipper.",
  variant = "section",
}: {
  eyebrow?: string;
  title?: string;
  lede?: string;
  variant?: "section" | "compact";
} = {}) {
  // Rendera inte sektionen förrän minst en medlem har ett riktigt namn satt.
  // Annars visas anonyma platshållare (roll-initialer) vilket underminerar
  // förtroendet sektionen är tänkt att bygga.
  const hasRealMembers = team.some((m) => m.name?.trim());
  if (!hasRealMembers) return null;
  const inner = (
    <div
      className={
        variant === "compact"
          ? "grid gap-4 sm:grid-cols-2"
          : "mt-10 grid gap-5 sm:grid-cols-2 md:grid-cols-3"
      }
    >
      {team.map((m) => {
        const displayName = m.name?.trim() || m.role;
        const initials = (m.name?.trim() || m.role)
          .split(" ")
          .map((w) => w.charAt(0))
          .join("")
          .slice(0, 2)
          .toUpperCase();
        return (
          <article
            key={m.role + m.name}
            className="flex items-start gap-4 rounded-3xl border border-white/10 bg-surface/60 p-5"
          >
            {m.photo ? (
              <img
                src={m.photo}
                alt={
                  m.name?.trim()
                    ? `${m.name.trim()}, ${m.role} på Fenny`
                    : `${m.role} på Fenny`
                }
                loading="lazy"
                width={64}
                height={64}
                className="h-16 w-16 shrink-0 rounded-full object-cover ring-1 ring-white/10"
              />
            ) : (
              <div
                aria-hidden
                className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-mint/15 text-lg font-semibold text-mint ring-1 ring-mint/30"
              >
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-base font-semibold leading-tight">
                {m.name?.trim() ? m.name : m.role}
              </div>
              {m.name?.trim() && (
                <div className="mt-0.5 text-xs uppercase tracking-widest text-mint">
                  {m.role}
                </div>
              )}
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {m.bio}
              </p>
              <span className="sr-only">{displayName}</span>
            </div>
          </article>
        );
      })}
    </div>
  );

  if (variant === "compact") {
    return (
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-mint">{eyebrow}</div>
        <h3 className="mt-2 text-balance text-xl font-semibold md:text-2xl">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{lede}</p>
        <div className="mt-6">{inner}</div>
      </div>
    );
  }

  return (
    <Section>
      <EyebrowHeading eyebrow={eyebrow} title={title} lede={lede} />
      {inner}
    </Section>
  );
}
